import { paths } from '@/routes/paths'
import { useUpdateLocationChecklistSettings } from '@/services'
import { useGoToPath } from '@/utils'
import { Button } from '@surein/ui'
import { MouseEvent } from 'react'

import { RiskChecklistResponse } from '@/types/location'

interface CheckItem {
  type: string
  [key: string]: any
}

interface ActionButton {
  [key: string]: any
}

interface Messages {
  [key: string]: any
}

interface ChecklistItem {
  type: string
  title: string
  text: string
  actionButtons: Record<string, ActionButton>
}

export const prepareChecklist = (
  messages: Messages,
  data: RiskChecklistResponse,
  actionButtons: Record<string, ActionButton>,
) => {
  const checks = data.checks || []

  return checks
    .map((item: CheckItem) => ({
      ...item,
      ...messages[item.type],
      actionButtons: actionButtons[item.type],
    }))
    .filter((item: ChecklistItem) => Boolean(item.title) && Boolean(item.text))
}

interface OnboardingButtonProps {
  locationId: string
  disabled?: boolean
  text: string
  onClick: () => void
}

export const OnboardingButton = ({
  disabled,
  text,
  onClick,
}: OnboardingButtonProps) => {
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    onClick?.()
  }

  return (
    <Button
      variant="primary"
      disabled={disabled}
      onClick={handleClick}
      size="sm"
      shape="square"
    >
      {text}
    </Button>
  )
}

interface PoaButtonProps {
  companyId: string
  disabled?: boolean
  text: string
}

export const PoaButton = ({ companyId, disabled, text }: PoaButtonProps) => {
  const goToPoa = useGoToPath(`/company/poa/${companyId}`)

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    goToPoa()
  }

  return (
    <Button
      variant="primary"
      disabled={disabled}
      onClick={handleClick}
      size="sm"
      shape="square"
    >
      {text}
    </Button>
  )
}

interface HasBoughtARecommendationButtonProps {
  locationId: string
  disabled?: boolean
  text: string
}

export const HasBoughtARecommendationButton = ({
  locationId,
  disabled,
  text,
}: HasBoughtARecommendationButtonProps) => {
  const goToFindInsurances = useGoToPath(
    paths.findInsurancesWithKey(locationId),
  )

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    goToFindInsurances()
  }

  return (
    <Button
      variant="primary"
      disabled={disabled}
      onClick={handleClick}
      size="sm"
      shape="square"
    >
      {text}
    </Button>
  )
}

interface InsuranceButtonsProps {
  locationId: string
  disabled?: boolean
  text: {
    text1: string
    text2: string
  }
}

export const InsuranceButtons = ({
  locationId,
  disabled,
  text,
}: InsuranceButtonsProps) => {
  const handleAddInsurance = useGoToPath(paths.addInsuranceContract)
  const updateLocationChecklistSettingsMutation = useUpdateLocationChecklistSettings(
    locationId,
  )

  const handleOnClick = () => updateLocationChecklistSettingsMutation.mutate({})

  const handleAddInsuranceClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    handleAddInsurance()
  }

  return (
    <>
      <Button
        variant="primary"
        disabled={disabled}
        onClick={handleAddInsuranceClick}
        size="sm"
        shape="square"
      >
        {text.text1}
      </Button>
      <Button
        variant="outline"
        disabled={disabled}
        onClick={handleOnClick}
        size="sm"
        shape="square"
      >
        {text.text2}
      </Button>
    </>
  )
}

interface ConnectAccountButtonProps {
  disabled?: boolean
  text: string
}

export const ConnectAccountButton = ({
  disabled,
  text,
}: ConnectAccountButtonProps) => {
  const goToConnect = useGoToPath(`/profile/integrations`)

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    goToConnect()
  }

  return (
    <Button
      variant="primary"
      disabled={disabled}
      onClick={handleClick}
      size="sm"
      shape="square"
    >
      {text}
    </Button>
  )
}
